import type { CSSProperties } from 'react'

export interface SkeletonProps {
  width?: number | string
  height?: number | string
  style?: CSSProperties
  // Number of pulsing skeleton lines stacked vertically. Useful for list pages.
  lines?: number
  // Per-line gap. Defaults to 6.
  gap?: number
}

const animationKeyframes = `
@keyframes pt-skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
`

// Inject the keyframes once globally (idempotent).
let injected = false
function ensureKeyframes(): void {
  if (injected || typeof document === 'undefined') return
  const style = document.createElement('style')
  style.textContent = animationKeyframes
  document.head.appendChild(style)
  injected = true
}

export function Skeleton({ width, height = 14, style, lines, gap = 6 }: SkeletonProps) {
  ensureKeyframes()
  const baseLine: CSSProperties = {
    background: 'linear-gradient(90deg, var(--color-surface) 0%, var(--color-surface-2) 50%, var(--color-surface) 100%)',
    backgroundSize: '200% 100%',
    animation: 'pt-skeleton-shimmer 1.5s ease-in-out infinite',
    height: typeof height === 'number' ? `${height}px` : height,
    borderRadius: 4,
    width: typeof width === 'number' ? `${width}px` : (width ?? '100%'),
    ...style,
  }
  if (!lines || lines <= 1) return <div style={baseLine} />
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} style={{ ...baseLine, width: i === lines - 1 ? '70%' : baseLine.width }} />
      ))}
    </div>
  )
}
